class VariantRadios extends HTMLElement {
  constructor() {
    super()

    // Options
    this.defaultOptions = {
      isQuickshop: false,
      collectionHandle: 'not-defined',
      productSoldOut: false,
      inventoryQuantityTreshold: 5,
    }

    // Get options from element data and combine with this.options
    if (this?.dataset?.options) {
      const dataOptions = JSON.parse(this.dataset.options)
      this.elementOptions = {
        ...this.defaultOptions,
        ...dataOptions,
      }
    }

    this.localStorageKey = 'size-storage-' + this.elementOptions.collectionHandle
    this.changeEvent = new Event('change')

    this.addEventListener('change', this.onVariantChange)

    // Check variant options from storage if not quickshop
    if (!this.elementOptions.isQuickshop) {
      this.checkOptionsStorage()
    }

    this.getSelectedOptions()
    this.updateOptions()
    this.updateVariantNotice()
  }

  onVariantChange() {
    this.getSelectedOptions()
    this.updateOptions()

    if (!this.elementOptions.isQuickshop) {
      this.setOptionsStorage()
    }

    // Guard if not all option values are selected
    if (this.options.some((item) => item === undefined)) {
      this.updateVariantNotice()
      return
    }

    this.toggleVariantNotice(true, false)
    this.updateMasterId()

    if (!this.elementOptions.isQuickshop) {
      this.toggleAddButton(true, '', false, false, false)
    } else {
      this.toggleAddButton(true, '', false, false, true)
    }

    // Guard if not currentVariant
    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true, false)
      this.setUnavailable()
      return
    }

    this.toggleMultiVariantStockNotice(false)
    if (!this.elementOptions.isQuickshop) {
      this.updateMedia()
      this.updateURL()
      this.updateVariantInput()
      this.renderProductInfo()
      this.dispatchVariantChange()
    } else {
      this.updateQuickshopFormProperties()
    }
  }

  checkOptionsStorage() {
    // Guard to see if localhost key exists
    if (Shopify.designMode || localStorage.getItem(this.localStorageKey) === null) {
      return
    }

    const localStorageData = localStorage.getItem(this.localStorageKey).split(',')
    const fieldsets = this.querySelectorAll('fieldset')

    // Loop through the localstorage
    localStorageData.forEach((localStorageDataItem, i) => {
      if (localStorageDataItem !== '') {
        const targetRadio = fieldsets[i].querySelector(`[data-value="${localStorageDataItem}"]`)
        if (targetRadio) {
          targetRadio.checked = true
        }
      }
    })

    // Dispatch a change event
    this.dispatchEvent(this.changeEvent)
  }

  getSelectedOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'))

    this.selectedOptionValues = fieldsets.map((fieldset) => {
      const elem = Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked)
      return elem?.value
    })

    this.updateSoldoutState(this.selectedOptionValues)
  }

  updateSoldoutState(selectedOptionValues = []) {
    const variants = this.getVariantData()
    const filteredVariantsByOption = []
    const optionSoldoutClass = 'options__option--soldout'

    selectedOptionValues.forEach((optionValue, i) => {
      const optionIndex = i + 1
      const newVariants = variants.map((variant) => ({ ...variant, optionIndex }))
      const filtered = optionValue ? newVariants.filter((variant) => variant[`option${optionIndex}`] === optionValue) : []

      filteredVariantsByOption.push(filtered)
    })

    const filteredVariantsByAvailabilty = filteredVariantsByOption.flat().filter((variant) => !variant.available)

    const fieldsets = Array.from(this.querySelectorAll('fieldset'))
    const allInputs = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input'))
    })

    // Remove all soldout classes
    if (fieldsets.length > 1) {
      allInputs.flat().forEach((input) => {
        input.closest('.options__option').classList.remove(optionSoldoutClass)
      })
    }

    filteredVariantsByAvailabilty.forEach(({ optionIndex, options }) => {
      const arrayIndex = optionIndex - 1
      const newOptions = [...options]
      const newFieldsets = [...fieldsets]

      newOptions.splice(arrayIndex, 1)
      newFieldsets.splice(arrayIndex, 1)

      newFieldsets.forEach((fieldset) => {
        const fields = fieldset.querySelectorAll(`input[value="${newOptions[0]}"]`)

        fields.forEach((field) => field.closest('.options__option').classList.add(optionSoldoutClass))
      })
    })
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'))
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked)?.value
    })
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option
        })
        .includes(false)
    })
  }

  dispatchVariantChange() {
    document.dispatchEvent(
      new CustomEvent('variant-change', {
        bubbles: true,
        variant: this.currentVariant,
        detail: {
          variant: this.currentVariant,
        },
      }),
    )
  }

  updateMedia() {
    if (!this.currentVariant || !this.currentVariant?.featured_media) return
    const newMedia = document.querySelector(`[data-media-id="${this.dataset.section}-${this.currentVariant.featured_media.id}"]`)
    if (!newMedia) return
    const parent = newMedia.parentElement
    parent.prepend(newMedia)
    window.setTimeout(() => {
      parent.scroll(0, 0)
    })
  }

  updateURL() {
    if (!this.currentVariant) return
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`)
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment`)
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]')
      input.value = this.currentVariant.id
      input.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }

  renderProductInfo() {
    fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`)
      .then((response) => response.text())
      .then((responseText) => {
        const id = `price-${this.dataset.section}`
        const html = new DOMParser().parseFromString(responseText, 'text/html')
        const destination = document.getElementById(id)
        const source = html.getElementById(id)

        if (source && destination) destination.innerHTML = source.innerHTML

        document.getElementById(`price-${this.dataset.section}`)?.classList.remove('visibility-hidden')
        if (this.currentVariant) {
          if (this.elementOptions.productSoldOut) {
            this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut, true, true, false)
          } else {
            this.toggleAddButton(!this.currentVariant.available, window.variantStrings.comingSoon, true, true, false)
          }
        }

        // Update the inventory quantity hidden input
        const inventoryQtyDestination = document.querySelector(`#product-form-${this.dataset.section}`).querySelector('input[name="inventory_quantity"]')
        const inventoryQtySource = html.querySelector(`#product-form-${this.dataset.section}`).querySelector('input[name="inventory_quantity"]')
        inventoryQtyDestination.value = inventoryQtySource.value

        this.handleMultiVariantStockNoticeContent(inventoryQtySource.value)
      })
  }

  handleMultiVariantStockNoticeContent(inventoryQuantity) {
    const stockNoticeElement = this.querySelector('.options__stock-notice')
    inventoryQuantity = parseInt(inventoryQuantity)
    if (stockNoticeElement && inventoryQuantity > 0 && inventoryQuantity <= this.elementOptions.inventoryQuantityTreshold) {
      const stockNoticeContent = this.elementOptions.inventoryQuantityLabel.replace('{{ count }}', inventoryQuantity)
      stockNoticeElement.innerHTML = stockNoticeContent

      this.toggleMultiVariantStockNotice(true)
    }
  }

  toggleMultiVariantStockNotice(showStockNotice) {
    const stockNoticeElement = this.querySelector('.options__stock-notice')

    if (stockNoticeElement) {
      if (showStockNotice) {
        stockNoticeElement.classList.add('options__stock-notice--visible')
      } else {
        stockNoticeElement.classList.remove('options__stock-notice--visible')
      }
    }
  }

  toggleAddButton(disable = true, text, modifyClass = true, add_soldout_class = false, quickshop = false) {
    let addButton
    if (quickshop) {
      addButton = this.closest('quick-shop').querySelector('[name="add"]')
    } else {
      addButton = document.getElementById(`product-form-${this.dataset.section}`)?.querySelector('[name="add"]')
    }
    const addButtonText = addButton?.querySelector('span')
    const addTrigger = document.querySelector('[data-atc-trigger]')

    if (!addButton) return

    if (quickshop) {
      if (this.currentVariant.available) {
        addButton.removeAttribute('disabled')
        addButtonText.textContent = window.variantStrings.addToCart
        addButton.classList.remove('button--disabled')
      } else {
        addButton.setAttribute('disabled', true)
        addButtonText.textContent = window.variantStrings.comingSoon
        addButton.classList.add('button--disabled')
      }
    } else {
      if (disable) {
        addButton.setAttribute('disabled', true)
        if (text) addButtonText.textContent = text
        if (add_soldout_class) addButton.classList.add('button--disabled')

        if (addTrigger) {
          if (text) addTrigger.textContent = text
          if (add_soldout_class) addTrigger.classList.add('button--disabled')
        }
      } else {
        addButton.removeAttribute('disabled')
        addButtonText.textContent = window.variantStrings.addToCart
        addButton.classList.remove('button--disabled')

        if (addTrigger) {
          addTrigger.textContent = window.variantStrings.addToCart
          addTrigger.classList.remove('button--disabled')
        }
      }
    }

    if (!modifyClass) return
  }

  setUnavailable() {
    const addButton = document.getElementById(`product-form-${this.dataset.section}`)?.querySelector('[name="add"]')
    if (!addButton) return
    addButton.textContent = window.variantStrings.unavailable
    document.getElementById(`price-${this.dataset.section}`)?.classList.add('visibility-hidden')
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent)
    return this.variantData
  }

  toggleVariantNotice(disable = true, highlight = true) {
    const variantNoticeElement = this.querySelector('.product-variant-notice')
    const variantNoticeHiddenClass = 'product-variant-notice--hidden'
    const variantNoticeErrorClass = 'product-variant-notice--error'

    disable === true ? variantNoticeElement?.classList.add(variantNoticeHiddenClass) : variantNoticeElement?.classList.remove(variantNoticeHiddenClass)
    highlight === true ? variantNoticeElement?.classList.add(variantNoticeErrorClass) : variantNoticeElement?.classList.remove(variantNoticeErrorClass)
  }

  updateVariantNotice() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'))
    const variantNoticeElement = this.querySelector('.product-variant-notice')

    fieldsets.forEach((fieldset) => {
      const fieldsetVariantNotice = fieldset.dataset?.variantFieldsetNotice

      if (fieldset.querySelector('input:checked') === null) {
        variantNoticeElement.innerHTML = fieldsetVariantNotice
      }
    })
  }

  setOptionsStorage() {
    localStorage.setItem(this.localStorageKey, this.selectedOptionValues)
  }

  updateQuickshopFormProperties() {
    const inventoryQuantitiesElement = this.querySelector('[data-inventory-quantities]')
    const variantQuantities = JSON.parse(inventoryQuantitiesElement.dataset.inventoryQuantities)
    const variantIdFormInput = this.closest('quick-shop').querySelector('[name="id"]')
    const variantInventoryQuantityFormInput = this.closest('quick-shop').querySelector('[name="inventory_quantity"]')
    const stockNoticeElement = this.querySelector('.options__stock-notice')

    if (variantIdFormInput) {
      variantIdFormInput.value = this.currentVariant.id
    }
    if (variantInventoryQuantityFormInput && this.currentVariant.id in variantQuantities) {
      variantInventoryQuantityFormInput.value = variantQuantities[this.currentVariant.id]
    }

    // Update the stock message
    if (stockNoticeElement && this.currentVariant.id in variantQuantities) {
      if (variantQuantities[this.currentVariant.id] > 0 && variantQuantities[this.currentVariant.id] <= this.elementOptions.inventoryQuantityTreshold) {
        let stockMessage = stockNoticeElement.dataset.noticeTranslation
        stockMessage = stockMessage.replace('{{ count }}', variantQuantities[this.currentVariant.id])
        stockNoticeElement.innerHTML = stockMessage
        stockNoticeElement.classList.add('options__stock-notice--visible')
      } else {
        stockNoticeElement.classList.remove('options__stock-notice--visible')
      }
    }
  }
}

if (!customElements.get('variant-radios')) {
  customElements.define('variant-radios', VariantRadios)
}
